import React from 'react';
import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import { Switch } from '@amzn/storm-ui';
import { useSelector, useDispatch } from 'react-redux';
import { selectEmbeddedChatOpen, setEmbeddedChatOpen } from 'src/app/slice';
import { setEmbeddedChatVisible } from 'src/commons/utils/a3cEmbeddedChat';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { HiddenContent } from './styles';

export const ADS_AGENT_OPEN_ICON_PATH =
  'https://m.media-amazon.com/images/G/01/CCX/svg/A3C_icon_Open.svg';
export const ADS_AGENT_CLOSE_ICON_PATH =
  'https://m.media-amazon.com/images/G/01/CCX/svg/A3C_icon_Close.svg';

export const AdsAgentButtonWrapper = styled.div<{ isNavOpen: boolean }>`
  min-height: 40px;
  width: 296px;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

export const AdsAgentDivider = styled.hr<{ isNavOpen: boolean }>`
  border: none;
  border-top: 2px solid ${({ theme }) => theme.border.color.subtle.default};
  margin: 12px 0px;
  margin-inline: 4px;
  width: ${({ isNavOpen }) => (isNavOpen ? 'calc(100% - 8px)' : '32px')};
`;

export const AdsAgentIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-inline-end: ${({ theme }) => theme.spacing.small.default};
`;

export const StyledSwitchWrapper = styled.div`
  margin-inline-start: auto;
  flex-shrink: 0;
`;

export const AdsAgentButtonInner = styled.div<{ isNavOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 40px;
  padding-inline: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} ${theme.spacing.xsmall.default}`};
  border-radius: ${({ theme }) => theme.border.radius.large};
  color: ${({ theme }) => theme.text.color.default};
  font: inherit;
`;

interface AdsAgentNavButtonProps {
  isNavOpen: boolean;
}

const AdsAgentNavButton: React.VFC<AdsAgentNavButtonProps> = ({
  isNavOpen,
}) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectEmbeddedChatOpen);
  const bundle = useSidenavBundle();

  const handleClick = () => {
    const nextState = !isOpen;
    dispatch(setEmbeddedChatOpen(nextState));
    setEmbeddedChatVisible(nextState);
  };

  const iconPath = isOpen
    ? ADS_AGENT_OPEN_ICON_PATH
    : ADS_AGENT_CLOSE_ICON_PATH;

  const label = bundle.getMessage('sidenav-ads-agent-label');
  const switchLabel = bundle.getMessage('sidenav-ads-agent-switch-label');

  return (
    <AdsAgentButtonWrapper isNavOpen={isNavOpen}>
      <AdsAgentButtonInner
        isNavOpen={isNavOpen}
        data-ccx-e2e-id="ads-agent-nav-button"
      >
        <AdsAgentIconWrapper>
          <SVG src={iconPath} width={24} height={24} />
        </AdsAgentIconWrapper>
        <HiddenContent alignItems="center" justifyContent="space-between">
          {label}
          <StyledSwitchWrapper>
            <Switch
              id="ads-agent-toggle"
              toggled={isOpen}
              onChange={handleClick}
              small
              switchLabel={switchLabel}
            />
          </StyledSwitchWrapper>
        </HiddenContent>
      </AdsAgentButtonInner>
      <AdsAgentDivider isNavOpen={isNavOpen} role="separator" />
    </AdsAgentButtonWrapper>
  );
};

export default AdsAgentNavButton;
